import {
  CityID,
  CityJSON,
  CurrencyFormatJSON,
  HostJSON,
  Minutes,
  PaymentOptionJSON,
  VatGroupJSON
} from "../backend.service";
import {Discount} from "./discount";
import {Menu} from "./menu";
import {OrderMethod} from "../constants";
import {getCurrentDate, round_to_minutes, round_to_quarterhours} from "../util";
import {DeliveryDay} from "../cart.service";

/** model classes for host etc. **/

export interface IOpeningHours {
  opensAt: Date,
  closesAt: Date,
  isOpen: boolean,
  opensIn: number,
  closesIn: number,
  willOpenSoon: boolean,
  willCloseSoon: boolean
}

export class Host {
  id: number;
  name: string;
  text: string;
  email: string;
  phone: string;
  domain: string;
  address: string;
  greeting: string;
  homepage: string;
  facebook_page: string;
  return_page: string;
  logo: string;
  session_maxage: number;
  allow_order: boolean;
  allow_waiter_call: boolean;
  merge_orders: boolean;
  merge_positions: boolean;
  simple_navigation: boolean;
  has_takeout: boolean;
  has_delivery: boolean;
  delivery_cities: Array<CityJSON>;
  open_times: [string, string];
  require_delivery_phone: boolean;
  require_delivery_email: boolean;
  takeout_preparation_time: Minutes;
  preorder_days: number;
  preorder_times: Array<{ day: number, label: string, times: [{ start: number, end: number }] }>;
  order_note_1: string;
  order_note_2: string;
  contact_data_inhouse: boolean;
  flat_mode: boolean;
  has_discount_codes: boolean;
  matomo_id: string;
  discounts: Array<Discount>;
  timezone: string;
  utc_offset: number;
  show_footer: boolean;
  show_footer_terms: boolean;
  locale: string;
  currency: string;
  currency_format: CurrencyFormatJSON;
  currency_symbol: string;
  i18n: Record<string, string>;
  feature_flags: string[];
  vat_groups: Array<VatGroupJSON>;

  constructor(init?: Partial<HostJSON>, menu?: Menu) {
    Object.assign(this, init);
    if (this.delivery_cities.length == 0) {
      this.has_delivery = false;
    }
    // handle discounts
    this.discounts = [];
    for (let i = 0; i < init.discounts.length; i++) {
      this.discounts.push(new Discount(init.discounts[i], menu));
    }
    if (menu) {
      menu.host = this;
    }
  }

  getMinMaxAmount(city_id?: CityID) {
    let qs = this.delivery_cities;
    if (city_id) {
      qs = qs.filter(city => city.id == city_id)
    }
    return {
      min: Math.min.apply(null, qs.map<number>(city => parseFloat(city.min_amount))),
      max: Math.max.apply(null, qs.map<number>(city => parseFloat(city.min_amount)))
    }
  }

  getMinMaxTime() {
    let qs = this.delivery_cities;
    return {
      min: Math.min.apply(null, qs.map<number>(city => city.duration)),
      max: Math.max.apply(null, qs.map<number>(city => city.duration))
    }
  }

  getDeliveryFee(city_id: CityID) {
    let qs = this.delivery_cities;
    if (qs.length == 0) {
      return {min: 0, max: 0};
    }
    if (city_id) {
      qs = qs.filter(city => city.id == city_id)
    }
    return {
      min: Math.min.apply(null, qs.map<number>(city => parseFloat(city.fee))),
      max: Math.max.apply(null, qs.map<number>(city => parseFloat(city.fee))),
    };
  }

  getCity(city_id: CityID) {
    return this.delivery_cities.find(city => {
      return city.id == city_id
    })
  }

  getOpeningHours(): IOpeningHours {

    const opensAt = new Date(this.open_times[0]),
          closesAt = new Date(this.open_times[1]),
          ts = getCurrentDate();

    if (this.open_times[0] == null && this.open_times[1] == null) {
      return {
        isOpen: true,
        opensAt: null,
        closesAt: null,
        opensIn: null,
        closesIn: null,
        willOpenSoon: false,
        willCloseSoon: false
      }
    }
    let isOpen = (opensAt < ts) && (closesAt > ts),
      opensIn = Math.floor((opensAt.getTime() - ts.getTime()) / (1000 * 60)),
      closesIn = Math.floor((closesAt.getTime() - ts.getTime()) / (1000 * 60)),
      willOpenSoon = !isOpen && opensIn > 0 && opensIn < 60,
      willCloseSoon = isOpen && closesIn < 45;

    // if opening in less than ten minutes, do as if we were open already
    if (willOpenSoon && (opensIn < 10)) {
      isOpen = true;
      willOpenSoon = false;
    }

    return {opensAt, closesAt, isOpen, opensIn, closesIn, willOpenSoon, willCloseSoon};
  }

  /**
   * true if this host allows preordering
   */
  canPreorder(): boolean {
    return this.preorder_days > 0 && this.preorder_times.length > 0;
  }

  /**
   * Get a list of possible preorder days and times with respect to the given
   * city and method.
   *
   * @param method OrderMethod
   * @param city: city id (for method=delivery; if not given, minimum time is assumed)
   *
   */
  getPreorderTimes(method: OrderMethod, city: CityID = null): Array<DeliveryDay> {
    const now = getCurrentDate().getTime(),
          result = new Array<DeliveryDay>();
    // determine prep time (in seconds)
    const prep_time = this.getPrepTime(method, city) * 60;
    // build a list of possible times
    this.preorder_times.forEach((entry) => {
      const times = [];
      entry.times.forEach((slot) => {
        let cur = round_to_quarterhours((slot.start  + prep_time)* 1000) / 1000;
        while (cur <= slot.end) {
          if (cur + entry.day > now/1000 + prep_time) {
            times.push(cur);
          }
          cur += 15*60;
        }
      });
      if (times.length > 0) {
        result.push({
          day: entry.day,
          label: entry.label,
          times: times
        })
      }
    });
    return result;
  }

  /**
   * calculate the preparation time for an order, taking method and city
   * into account. If city is not given, the zone with the lowest delivery
   * time is assumed.
   *
   * @param method  order method
   * @param city    city
   * @returns preparation time in minutes
   */
  getPrepTime(method: OrderMethod, city?: CityID): Minutes {
    if (method == OrderMethod.takeout) {
      return this.takeout_preparation_time;
    } else if (method == OrderMethod.delivery) {
      if (city) {
        return this.getCity(city).duration;
      } else {
        return this.getMinMaxTime().min;
      }
    } else {
      return 0; // inhouse order
    }
  }

  /**
   * calculate the earliest date where an order can be fulfilled, taking
   * prepTime into account. The date gets rounded to full minutes.
   *
   * @param method  order method
   * @param city    city
   */
  getEarliestDate(method: OrderMethod, city?: CityID): Date {
    const prep = this.getPrepTime(method, city),
          oh = this.getOpeningHours(),
          now = getCurrentDate();

    let expected;

    if (oh.isOpen) {
      expected = new Date(round_to_minutes(now.getTime() + prep * 60 * 1000));
      // is this after closing time?
      if (oh.closesAt && expected > oh.closesAt) {
        expected = null;
      }
    } else {
      expected = (oh.opensAt) ? new Date(round_to_quarterhours(oh.opensAt.getTime() + prep * 60 * 1000)) : null;
      // is this in the past? this can happen if we load the page before closing,
      // but became closed in the meantime
      if (expected < new Date(now)) {
        expected = null;
      }
    }
    // We don't have a viable edd yet, try whether we can preorder at least
    if (expected == null) {
      const pot = this.getPreorderTimes(method, city);
      if (pot.length > 0) {
        return new Date(1000 * (pot[0].day + pot[0].times[0]));
      } else {
        return null;  // it's not possible to order right now
      }
    } else {
      return expected;
    }
  }
}
